/**
 * /dashboard — the HR landing page (ADR-0015 Phase 1). Headcount tiles + the
 * org-wide "needs attention" approval counts + an upcoming-celebrations widget
 * (read-only here; the HR-clicks-Send email is Phase 2). owner/admin/hr see
 * everything; finance sees the headcount tiles only (the role tier, D-1a).
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { getDashboardSummary, previewCelebration, sendCelebration, type CelebrationItem, type OnLeaveEntry, type PayrollPulse } from '../api/dashboard.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { useAuth } from '../auth/AuthContext.js';
import { AssetDashboard } from './assetDashboard.js';

export const dashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const MODEL_LABEL: Record<string, string> = { local_employee: 'Local', international_remote: 'International remote' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDay(isoDate: string): string {
  const [, m, d] = isoDate.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1]} ${d}`;
}
function whenLabel(daysUntil: number): string {
  if (daysUntil === 0) return 'today';
  if (daysUntil === 1) return 'tomorrow';
  return `in ${daysUntil} days`;
}

function DashboardPage(): JSX.Element {
  const { user } = useAuth();
  // The `it` custodian role can't read the HR dashboard summary (owner/admin/hr/
  // finance-gated) and shouldn't — it sees an asset-only dashboard instead
  // (ADR-0025). Every other role gets the HR dashboard below.
  if (user?.role === 'it') return <AssetDashboard />;

  return <HrDashboard />;
}

function HrDashboard(): JSX.Element {
  const query = useQuery({ queryKey: ['dashboard', 'summary'], queryFn: () => getDashboardSummary() });

  return (
    <section className="p-8 space-y-8 max-w-5xl">
      <header>
        <h1 className="font-serif text-3xl text-white">Dashboard</h1>
        <p className="text-sm text-gray-400">Your team at a glance.</p>
      </header>

      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load the dashboard.</Placeholder>
      ) : (
        <>
          {/* Headcount — all roles */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Employees" value={query.data!.headcount.totalStaff} />
            <Stat label="On leave today" value={query.data!.headcount.onLeaveToday} />
            <Stat label="New hires this month" value={query.data!.headcount.newHiresThisMonth} />
            <BreakdownStat label="By country" map={query.data!.headcount.byCountry} />
          </div>
          <BreakdownLine label="Payroll model" map={query.data!.headcount.byPayrollModel} relabel={MODEL_LABEL} />

          {/* Payroll pulse — all roles (finance's home) */}
          <PayrollPulseCard pulse={query.data!.payrollPulse} />

          {/* Needs attention — owner/admin/hr only */}
          {query.data!.pendingApprovals ? (
            <div className="space-y-3">
              <h2 className="text-sm uppercase tracking-wide text-gray-500">Needs attention</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <ActionCard label="Leave requests" count={query.data!.pendingApprovals.leave} to="/leave/approvals" />
                <ActionCard label="Overtime" count={query.data!.pendingApprovals.overtime} to="/attendance/overtime/approvals" />
                <ActionCard label="Recruitment" count={query.data!.pendingApprovals.recruitment} to="/recruitment/approvals" />
                <ActionCard label="Reviews due" count={query.data!.pendingApprovals.performance} to="/performance/reviews" />
              </div>
            </div>
          ) : null}

          {/* Recruitment pulse — owner/admin/hr only */}
          {query.data!.recruitmentPulse ? (
            <div className="space-y-3">
              <h2 className="text-sm uppercase tracking-wide text-gray-500">Recruitment</h2>
              <div className="grid grid-cols-3 gap-4">
                <Stat label="Open roles" value={query.data!.recruitmentPulse.openRequisitions} />
                <Stat label="In pipeline" value={query.data!.recruitmentPulse.inPipeline} />
                <Stat label="Offers out" value={query.data!.recruitmentPulse.offersOut} />
              </div>
              <Link to="/recruitment/requisitions" className="text-xs text-gray-400 underline">Open recruitment →</Link>
            </div>
          ) : null}

          {/* On leave this week — owner/admin/hr only */}
          {query.data!.onLeaveThisWeek ? <OnLeaveStrip entries={query.data!.onLeaveThisWeek} /> : null}

          {/* Celebrations — owner/admin/hr only */}
          {query.data!.celebrations ? (
            <CelebrationsWidget items={query.data!.celebrations.items} windowDays={query.data!.celebrations.windowDays} />
          ) : null}
        </>
      )}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <article className="rounded border border-gray-800 bg-gray-950 p-5">
      <p className="text-3xl text-white tabular-nums">{value}</p>
      <p className="text-xs text-gray-400 mt-1">{label}</p>
    </article>
  );
}

function BreakdownStat({ label, map }: { label: string; map: Record<string, number> }): JSX.Element {
  const entries = Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <article className="rounded border border-gray-800 bg-gray-950 p-5">
      <p className="text-sm text-white tabular-nums">{entries.map(([k, v]) => `${k} ${v}`).join(' · ') || '—'}</p>
      <p className="text-xs text-gray-400 mt-1">{label}</p>
    </article>
  );
}

function BreakdownLine({ label, map, relabel }: { label: string; map: Record<string, number>; relabel: Record<string, string> }): JSX.Element {
  const entries = Object.entries(map);
  if (entries.length === 0) return <></>;
  return (
    <p className="text-xs text-gray-500">
      {label}: {entries.map(([k, v]) => `${relabel[k] ?? k} ${v}`).join(' · ')}
    </p>
  );
}

type ActionTo = '/leave/approvals' | '/attendance/overtime/approvals' | '/recruitment/approvals' | '/performance/reviews';
function ActionCard({ label, count, to }: { label: string; count: number; to: ActionTo }): JSX.Element {
  const active = count > 0;
  return (
    <Link
      to={to}
      className={`rounded border p-5 block hover:border-gray-600 ${active ? 'border-gray-700 bg-gray-900' : 'border-gray-800 bg-gray-950'}`}
    >
      <p className={`text-3xl tabular-nums ${active ? 'text-white' : 'text-gray-600'}`}>{count}</p>
      <p className="text-xs text-gray-400 mt-1">{label} awaiting</p>
    </Link>
  );
}

function CelebrationsWidget({ items, windowDays }: { items: CelebrationItem[]; windowDays: number }): JSX.Element {
  const [target, setTarget] = useState<CelebrationItem | null>(null);
  return (
    <div className="space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">Coming up — birthdays &amp; anniversaries</h2>
      {items.length === 0 ? (
        <Placeholder>Nothing in the next {windowDays} days.</Placeholder>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={`${it.employeeId}-${it.occasion}`} className="rounded border border-gray-900 p-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-white">
                  {it.name}
                  {it.occasion === 'work_anniversary' && it.isMilestone ? (
                    <span className="ml-2 rounded px-2 py-0.5 text-xs border border-yellow-800 text-yellow-300">milestone</span>
                  ) : null}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {it.occasion === 'birthday'
                    ? `Birthday — ${fmtDay(it.date)}`
                    : `${it.years} year${it.years === 1 ? '' : 's'} — ${fmtDay(it.date)}`}
                  {' · '}
                  {whenLabel(it.daysUntil)}
                </p>
              </div>
              <div className="shrink-0">
                {it.sent ? (
                  <span className="text-xs text-green-400">Sent ✓</span>
                ) : !it.hasEmail ? (
                  <span className="text-xs text-gray-600">no email on file</span>
                ) : (
                  <button type="button" onClick={() => setTarget(it)} className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900 text-gray-200">
                    Send
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {target ? <CelebrationSendModal item={target} onClose={() => setTarget(null)} /> : null}
    </div>
  );
}

function CelebrationSendModal({ item, onClose }: { item: CelebrationItem; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['celebration-preview', item.employeeId, item.occasion],
    queryFn: () => previewCelebration(item.employeeId, item.occasion),
  });
  // Seed the editable fields once the default arrives.
  useEffect(() => {
    if (preview.data) {
      setSubject(preview.data.subject);
      setBody(preview.data.body);
    }
  }, [preview.data]);

  const send = useMutation({
    mutationFn: () => sendCelebration(item.employeeId, { occasion: item.occasion, subject: subject.trim(), body: body.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not send the email.'),
  });

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="celeb-modal-title" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="w-full max-w-lg rounded border border-gray-800 bg-gray-950 p-6 space-y-4">
        <header className="space-y-1">
          <h2 id="celeb-modal-title" className="font-serif text-xl text-white">
            {item.occasion === 'birthday' ? 'Send a birthday note' : 'Send an anniversary note'}
          </h2>
          <p className="text-sm text-gray-400">To {item.name}. Edit the message if you like, then send.</p>
        </header>

        {preview.isLoading ? (
          <p className="text-sm text-gray-500">Preparing…</p>
        ) : preview.isError ? (
          <Alert variant="error">Could not load the message.</Alert>
        ) : (
          <div className="space-y-3">
            <label className="block text-sm text-gray-300 space-y-1">
              <span>Subject</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
            </label>
            <label className="block text-sm text-gray-300 space-y-1">
              <span>Message</span>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} aria-label="Message" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
            </label>
          </div>
        )}

        {error ? <Alert variant="error">{error}</Alert> : null}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={send.isPending} className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-900 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { setError(null); send.mutate(); }}
            disabled={send.isPending || preview.isLoading || subject.trim() === '' || body.trim() === ''}
            className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
          >
            {send.isPending ? 'Sending…' : 'Send email'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayrollPulseCard({ pulse }: { pulse: PayrollPulse }): JSX.Element {
  return (
    <div className="space-y-2">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">Payroll</h2>
      <div className="rounded border border-gray-800 bg-gray-950 p-5 flex items-center justify-between gap-4">
        <div>
          {pulse.lastRun ? (
            <>
              <p className="text-sm text-white">Last run · {pulse.lastRun.status}</p>
              <p className="text-xs text-gray-400 mt-1">
                {pulse.lastRun.periodStart.slice(0, 10)} → {pulse.lastRun.periodEnd.slice(0, 10)} · pays {pulse.lastRun.payDate.slice(0, 10)}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">No payroll runs yet.</p>
          )}
        </div>
        {pulse.awaitingApproval > 0 ? (
          <span className="shrink-0 rounded px-2 py-1 text-xs border border-yellow-800 text-yellow-300">{pulse.awaitingApproval} awaiting approval</span>
        ) : null}
      </div>
      <Link to="/payroll-runs" className="text-xs text-gray-400 underline">Open payroll runs →</Link>
    </div>
  );
}

function OnLeaveStrip({ entries }: { entries: OnLeaveEntry[] }): JSX.Element {
  return (
    <div className="space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-gray-500">On leave this week</h2>
      {entries.length === 0 ? (
        <Placeholder>Nobody is on leave in the next 7 days.</Placeholder>
      ) : (
        <ul className="space-y-2">
          {entries.map((e, i) => (
            <li key={`${e.name}-${i}`} className="rounded border border-gray-900 p-3 text-sm flex items-center justify-between gap-4">
              <span className="text-white">{e.name}</span>
              <span className="text-xs text-gray-400">{e.leaveType} · {e.startDate.slice(0, 10)} → {e.endDate.slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
